import React, { useEffect, useState } from "react";
import Layout, { Container } from "../components/layout";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { apiurl } from "../config/config";
import { SuwasthiLoader } from "../components/loader";
import Pagination from "../components/pagination";
import { FaPlus } from "react-icons/fa";
import { getCookie } from "../config/webStorage";

const MATERIAL_CATEGORIES = {
  packaging_material: [
    { value: "jar", label: "Jar" },
    { value: "tape", label: "Tape" },
    { value: "carton_box", label: "Carton Box" },
    { value: "spoon", label: "Spoon" },
    { value: "diet_chart", label: "Diet Chart" },
    { value: "shrink", label: "Shrink" },
    { value: "monocarton", label: "Monocarton" },
    { value: "label", label: "Label" },
    { value: "gel", label: "Gel" },
    { value: "bag", label: "Bag" },
    { value: "empty_capsule", label: "Empty Capsule" },
  ],
  raw_material: [
    { value: "herb", label: "Herb" },
    { value: "powder", label: "Powder" },
    { value: "extract", label: "Extract" },
    { value: "chemical", label: "Chemical" },
    { value: "oil", label: "Oil" },
    { value: "juice", label: "Juice" },
    { value: "ghee", label: "Ghee" },
  ],
};

const Materials = () => {
  const token = getCookie("SWFMSToken");
  const [searchParams, setSearchParams] = useSearchParams();

  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [materialType, setMaterialType] = useState(searchParams.get("materialType") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [stockStatus, setStockStatus] = useState(searchParams.get("stockStatus") || "");
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [limit, setLimit] = useState(Number(searchParams.get("limit")) || 100);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${apiurl}/api/material/get`, {
          params: { page, limit, search, materialType, category, stockStatus },
          headers: { Authorization: token },
        });
        setMaterials(res.data.data || []);
        setTotalItems(res.data.pagination.totalItems || 0);
        setCurrentPage(res.data.pagination.currentPage || 1);
      } catch (err) {
        console.error("Material fetch failed", err);
      } finally {
        setLoading(false);
      }
    };
    fetchMaterials();
  }, [page, limit, search, materialType, category, stockStatus, token]);

  const applyFilters = () => {
    setPage(1);
    setSearchParams({ page: 1, limit, search, materialType, category, stockStatus });
  };

  const resetFilters = () => {
    setSearch("");
    setMaterialType("");
    setCategory("");
    setStockStatus("");
    setPage(1);
    setSearchParams({ page: 1, limit });
  };

  const stockBadge = (status) => {
    if (status === "in_stock") return "bg-green-100 text-green-700";
    if (status === "low_stock") return "bg-yellow-100 text-yellow-700";
    if (status === "out_of_stock") return "bg-red-100 text-red-700";
    return "bg-gray-100 text-gray-600";
  };

  return (
    <Layout>
      <Container overflow="overflow-scroll">
        <div className="p-4 bg-gray-100 min-h-screen">
          {/* Top Bar */}
          <div className="bg-white p-4 rounded shadow mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex gap-2 w-full md:w-1/2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search material..."
                className="p-2 border rounded w-full"
              />
              <button onClick={applyFilters} className="px-4 py-2 bg-[#4B49AC] text-white rounded">
                Search
              </button>
            </div>

            <div className="flex gap-2 items-center">
              <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="p-2 border rounded">
                {[100, 200, 500, 1000].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <Link to="/materials/create" className="flex items-center gap-2 px-4 py-2 bg-[#4B49AC] text-white rounded">
                <FaPlus /> New
              </Link>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <select
              value={materialType}
              onChange={(e) => { setMaterialType(e.target.value); setCategory(""); }}
              className="p-2 border rounded"
            >
              <option value="">All Types</option>
              <option value="raw_material">Raw Material</option>
              <option value="packaging_material">Packaging Material</option>
            </select>

            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="p-2 border rounded"
            >
              <option value="">All Categories</option>
              {materialType
                ? MATERIAL_CATEGORIES[materialType]?.map((cat) => <option key={cat.value} value={cat.value}>{cat.label}</option>)
                : Object.values(MATERIAL_CATEGORIES).flat().map((cat) => <option key={cat.value} value={cat.value}>{cat.label}</option>)
              }
            </select>

            <select
              value={stockStatus}
              onChange={(e) => setStockStatus(e.target.value)}
              className="p-2 border rounded"
            >
              <option value="">All Stock</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>

            <button onClick={applyFilters} className="bg-[#4B49AC] text-white rounded px-4">Apply</button>
            <button onClick={resetFilters} className="bg-gray-200 rounded px-4">Reset</button>
          </div>

          {/* Table */}
          <div className="bg-white rounded shadow">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="font-semibold text-lg">Materials</h2>
              <span className="text-sm text-gray-600">
                Total Items: <b>{totalItems}</b>
              </span>
            </div>

            {loading ? (
              <SuwasthiLoader />
            ) : materials.length === 0 ? (
              <p className="p-4 text-red-600">No materials found.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 border">#</th>
                        <th className="p-2 border">Material ID</th>
                        <th className="p-2 border">Name</th>
                        <th className="p-2 border">Type</th>
                        <th className="p-2 border">Category</th>
                        <th className="p-2 border">Unit</th>
                        <th className="p-2 border">Qty</th>
                        <th className="p-2 border">Stock</th>
                        <th className="p-2 border">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materials.map((item, idx) => (
                        <tr key={item._id} className="hover:bg-gray-50">
                          <td className="p-2 border text-center">{(currentPage - 1) * limit + idx + 1}</td>
                          <td className="p-2 border">{item.materialId}</td>
                          <td className="p-2 border font-medium capitalize">{item.name}</td>
                          <td className="p-2 border">{item.materialType}</td>
                          <td className="p-2 border">{item.category}</td>
                          <td className="p-2 border">{item.unit}</td>
                          <td className="p-2 border text-right">{item.quantity}</td>
                          <td className="p-2 border text-center">
                            <span className={`px-2 py-1 rounded text-xs ${stockBadge(item.stockStatus)}`}>
                              {item.stockStatus}
                            </span>
                          </td>
                          <td className="p-2 border text-center">
                            <Link to={`/materials/${item.materialId}`} className="px-3 py-1 bg-[#4B49AC] text-white rounded text-sm">
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={limit}
                  onPageChange={(p) => {
                    setPage(p);
                    setSearchParams({ page: p, limit, search, materialType, category, stockStatus });
                  }}
                />
              </>
            )}
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default Materials;